import { useState } from 'react';
import { avisar, avisarErro } from './Avisos.tsx';
import { Icone } from './Icone.tsx';
import { ErroDaApi, apiRelatorios, type Relatorio } from '../lib/api.ts';
import { paraTela } from '../lib/datas.ts';

/**
 * ⭐⭐ O PERÍODO DE APURAÇÃO E O HISTÓRICO — 20/09/2026 (PRD §50).
 *
 * > *"Precisa ter essa informação no relatório, falando que as informações do
 * > relatório foram extraídas da data que iniciamos o relatório até a data que
 * > finalizarmos. Inclusive, o HUB precisa registrar essas questões e ter o
 * > histórico."* — Richard
 *
 * ⭐ **E responde a outra pergunta dele, do mesmo dia:** *"quando finalizo o
 * relatório? Pois sempre quando baixo o relatório para conferir algo, aparece
 * rascunho"*. A resposta é que **a entrega é pelo CASO** (decisão de
 * 14/09/2026) — e até aqui **nenhuma tela dizia isso**. O aviso de travado
 * explicava como DESTRAVAR; nada explicava como ENTREGAR.
 *
 * ⚠️ **Regra que não se dá para adivinhar precisa estar escrita na tela onde
 * ela morde.** É a mesma lição de `sincronizar`: método sem tela é defeito.
 */
export function ApuracaoDoRelatorio({
  r,
  ehMaster,
  aoMudar,
  aoAbrirCliente,
}: {
  r: Relatorio;
  ehMaster: boolean;
  aoMudar: (novo: Relatorio) => void;
  /** ⭐ Leva à ficha do cliente, que é onde a etapa do caso se move. */
  aoAbrirCliente: () => void;
}) {
  const [editando, setEditando] = useState(false);
  const [data, setData] = useState(r.apuracaoDe ?? r.criadoEm.slice(0, 10));
  const [salvando, setSalvando] = useState(false);

  const inicio = r.apuracaoDe ?? r.criadoEm.slice(0, 10);
  const travado = r.situacao === 'entregue';

  async function salvar() {
    setSalvando(true);
    try {
      aoMudar(await apiRelatorios.editar(r.id, { apuracaoDe: data }));
      setEditando(false);
      avisar('Início da apuração corrigido.');
    } catch (e) {
      avisarErro(e instanceof ErroDaApi ? e.message : 'Não consegui salvar a data.');
    } finally {
      setSalvando(false);
    }
  }

  const QUANDO: Record<string, string> = {
    criado: 'Relatório aberto',
    entregue: 'Entregue ao cliente',
    reaberto: 'Reaberto para ajuste',
  };

  return (
    <section className="s-folha rel-apuracao">
      <header className="s-folha-topo">
        <h2 className="s-folha-titulo">Apuração e histórico</h2>
        <p className="rel-apuracao-nota">Sai na contracapa do documento, como “Período das extrações”.</p>
      </header>
      <div className="s-folha-corpo">
        <dl className="s-props">
          <div className="s-prop">
            <dt>Apuração desde</dt>
            <dd>
              {editando ? (
                <span className="rel-apuracao-editar">
                  <input type="date" value={data} onChange={(e) => setData(e.target.value)} />
                  <button type="button" className="imv-bt primario" disabled={salvando} onClick={() => void salvar()}>
                    <Icone nome="check" tamanho={13} /> {salvando ? 'Salvando…' : 'Salvar'}
                  </button>
                  <button type="button" className="com-desfazer" onClick={() => setEditando(false)}>cancelar</button>
                </span>
              ) : (
                <>
                  <span className="dado">{paraTela(inicio)}</span>
                  {!r.apuracaoDe && <span className="rel-apuracao-padrao">a data em que o relatório foi aberto</span>}
                  {/* ⛔ Corrigir a data é do master: ela vai impressa e o documento vai a juízo. */}
                  {ehMaster && !travado && (
                    <button type="button" className="com-desfazer" onClick={() => setEditando(true)}>corrigir</button>
                  )}
                </>
              )}
            </dd>
          </div>
          <div className="s-prop">
            <dt>Apuração até</dt>
            <dd>
              {r.entregueEm ? (
                <span className="dado">{paraTela(r.entregueEm)}</span>
              ) : (
                <i>em curso — a data final é a da entrega</i>
              )}
            </dd>
          </div>
          {/* ⭐ O eco do que sai impresso: quem corrige a data vê o resultado sem gerar o PDF. */}
          <div className="s-prop">
            <dt>Sai impresso</dt>
            <dd className="dado">
              {r.entregueEm ? `${paraTela(inicio)} a ${paraTela(r.entregueEm)}` : `desde ${paraTela(inicio)} · em curso`}
            </dd>
          </div>
        </dl>

        {/*
          ⭐⭐ A resposta à pergunta dele. ⚠️ Só enquanto NÃO está entregue:
          depois, o aviso de travado já diz o caminho de volta.
        */}
        {!travado && (
          <p className="ajuda rel-como-entregar">
            <Icone nome="atencao" tamanho={14} />
            <span>
              <strong>Enquanto não for entregue, o documento sai marcado como rascunho.</strong> Para
              finalizar, abra a ficha do cliente, ache o cartão deste caso e mude a etapa para{' '}
              <strong>Entregue</strong> no seletor do cartão. É isso que carimba a data, tira a tarja e
              trava o relatório.{' '}
              {/*
                ⚠️ **O atalho leva; NÃO entrega.** A entrega é pelo caso desde 14/09/2026, decisão
                dele — um botão aqui que movesse a etapa desfaria essa decisão pela porta dos fundos.
                O que se encurta é o caminho, não a regra.
              */}
              <button type="button" className="com-link" onClick={aoAbrirCliente}>
                Abrir a ficha do cliente
              </button>
            </span>
          </p>
        )}

        {(r.eventos ?? []).length > 0 && (
          <ul className="rel-historico">
            {(r.eventos ?? []).map((e) => (
              <li key={e.id}>
                <span className="rel-historico-quando dado">{paraTela(e.em.slice(0, 10))}</span>
                <span className="rel-historico-que">{QUANDO[e.tipo] ?? e.tipo}</span>
                <span className="rel-historico-quem">{e.por}</span>
                {e.observacao && <p>{e.observacao}</p>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
